'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FamilyPreferences {
  householdSize: number;
  dietaryRestrictions: string[];
  preferredFishTypes: string[];
  weeklyConsumption: string;
  cookingPreference: string;
}

interface FamilyPreferencesSectionProps {
  initialPreferences: FamilyPreferences;
}

const FamilyPreferencesSection = ({ initialPreferences }: FamilyPreferencesSectionProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [preferences, setPreferences] = useState<FamilyPreferences>(initialPreferences);

  const dietaryOptions = ['None', 'Vegetarian', 'Pescatarian', 'Halal', 'Kosher', 'Low Mercury'];
  const fishTypes = ['Salmon', 'Tuna', 'Cod', 'Shrimp', 'Prawns', 'Mackerel', 'Sardines', 'Pomfret'];
  const consumptionOptions = ['1-2 kg', '2-4 kg', '4-6 kg', '6-8 kg', '8+ kg'];
  const cookingOptions = ['Curry', 'Fried', 'Grilled', 'Steamed', 'Raw/Sushi'];

  const toggleDietaryRestriction = (option: string) => {
    setPreferences({
      ...preferences,
      dietaryRestrictions: preferences.dietaryRestrictions.includes(option)
        ? preferences.dietaryRestrictions.filter((r) => r !== option)
        : [...preferences.dietaryRestrictions, option],
    });
  };

  const toggleFishType = (type: string) => {
    setPreferences({
      ...preferences,
      preferredFishTypes: preferences.preferredFishTypes.includes(type)
        ? preferences.preferredFishTypes.filter((t) => t !== type)
        : [...preferences.preferredFishTypes, type],
    });
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 shadow-elevation-sm">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 bg-secondary/10 rounded-lg">
            <Icon name="UserGroupIcon" size={24} className="text-secondary" />
          </div>
          <div>
            <h2 className="font-heading font-semibold text-lg text-foreground">Family Preferences</h2>
            <p className="text-sm text-muted-foreground">Personalize your recommendations</p>
          </div>
        </div>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md font-medium text-sm transition-smooth hover:shadow-elevation-md hover-lift"
          >
            <Icon name="PencilIcon" size={16} />
            <span>Edit</span>
          </button>
        )}
      </div>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-foreground mb-3">Household Size</label>
          {isEditing ? (
            <div className="flex items-center gap-4">
              <button
                onClick={() => setPreferences({ ...preferences, householdSize: Math.max(1, preferences.householdSize - 1) })}
                className="w-10 h-10 flex items-center justify-center bg-muted rounded-md transition-smooth hover:bg-border"
              >
                <Icon name="MinusIcon" size={20} />
              </button>
              <span className="font-data font-semibold text-2xl text-foreground w-12 text-center">
                {preferences.householdSize}
              </span>
              <button
                onClick={() => setPreferences({ ...preferences, householdSize: preferences.householdSize + 1 })}
                className="w-10 h-10 flex items-center justify-center bg-muted rounded-md transition-smooth hover:bg-border"
              >
                <Icon name="PlusIcon" size={20} />
              </button>
              <span className="text-sm text-muted-foreground">members</span>
            </div>
          ) : (
            <p className="text-sm text-foreground font-medium">{preferences.householdSize} members</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-3">Dietary Restrictions</label>
          {isEditing ? (
            <div className="flex flex-wrap gap-2">
              {dietaryOptions.map((option) => (
                <button
                  key={option}
                  onClick={() => toggleDietaryRestriction(option)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-smooth ${
                    preferences.dietaryRestrictions.includes(option)
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-foreground hover:bg-border'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-foreground font-medium">
              {preferences.dietaryRestrictions.length > 0
                ? preferences.dietaryRestrictions.join(', ')
                : 'None specified'}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-3">Preferred Fish Types</label>
          {isEditing ? (
            <div className="flex flex-wrap gap-2">
              {fishTypes.map((type) => (
                <button
                  key={type}
                  onClick={() => toggleFishType(type)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-smooth ${
                    preferences.preferredFishTypes.includes(type)
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-foreground hover:bg-border'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-foreground font-medium">
              {preferences.preferredFishTypes.length > 0
                ? preferences.preferredFishTypes.join(', ')
                : 'All types'}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-3">Weekly Consumption</label>
          {isEditing ? (
            <select
              value={preferences.weeklyConsumption}
              onChange={(e) => setPreferences({ ...preferences, weeklyConsumption: e.target.value })}
              className="w-full px-4 py-2 bg-background border border-input rounded-md text-sm focus-ring"
            >
              {consumptionOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          ) : (
            <p className="text-sm text-foreground font-medium">{preferences.weeklyConsumption}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-3">Cooking Preference</label>
          {isEditing ? (
            <select
              value={preferences.cookingPreference}
              onChange={(e) => setPreferences({ ...preferences, cookingPreference: e.target.value })}
              className="w-full px-4 py-2 bg-background border border-input rounded-md text-sm focus-ring"
            >
              {cookingOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          ) : (
            <p className="text-sm text-foreground font-medium">{preferences.cookingPreference}</p>
          )}
        </div>
      </div>

      {isEditing && (
        <div className="flex items-center gap-3 mt-6 pt-6 border-t border-border">
          <button
            onClick={() => setIsEditing(false)}
            className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-md font-medium text-sm transition-smooth hover:shadow-elevation-md hover-lift"
          >
            Save Preferences
          </button>
          <button
            onClick={() => {
              setPreferences(initialPreferences);
              setIsEditing(false);
            }}
            className="flex-1 px-4 py-2 bg-muted text-foreground rounded-md font-medium text-sm transition-smooth hover:bg-border"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default FamilyPreferencesSection;